import React, { PureComponent } from 'react';
import { Helmet } from 'react-helmet';
import { makeStyles } from '@material-ui/core/styles';
import Pagination from '@material-ui/lab/Pagination';
import { graphql } from "@apollo/client/react/hoc";
import compose from 'lodash/flowRight';
import debounce from 'lodash/debounce';
import { withState } from '../../../lib/StateFully';
import authManager, {downloadWithAuth, PERMISSIONS} from '../../../lib/authManager';
import { withInstallation } from '../../../components/InstallationContext';
import { Button } from '../../../components/Button';
import { LoadingFork } from '../../../components/LoadingFork';
import { SearchInput } from '../../../components/SearchInput';
import { CreateForm } from './CreateForm';

import {
    sanctionedPlatesByInstallationQuery,
    updateSanctionedPlateQuery,
    deleteSanctionedPlateQuery,
    installationPinpadEnabledQuery,
    sanctionedPlateContactsQuery,
} from './queries';
import { DRIVO_URL } from "../../../lib/drivoApollo";
import { PlateRow } from "./PlateRow";
import { allLots } from "../ParkingLots/queries";
import moment from 'moment';
import {PermissionOnly} from "../../../components/AdminOnly";
import Panel from '../../Concierge/components/Panel';
import VipPageShell, { HeaderCount } from '../../Concierge/vip/components/VipPageShell';
import { T } from '../../Concierge/constants';

/*
 * Plates are searched and paged on the server. The biggest sites hold 800+
 * plates and rendering (or filtering) all of them at once froze the tab.
 */
const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

const EMPTY_PAGE = { items: [], total: 0, page: 1, pageSize: PAGE_SIZE, totalPages: 0 };

const useStyles = makeStyles({
    stack: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 360px',
        gap: 24,
        alignItems: 'start',
        '@media (max-width: 1100px)': { gridTemplateColumns: '1fr' },
    },
    createPanel: { gridColumn: 2, gridRow: 1, '@media (max-width: 1100px)': { gridColumn: 'auto', gridRow: 2 } },
    listPanel: { gridColumn: 1, gridRow: 1, '@media (max-width: 1100px)': { gridColumn: 'auto', gridRow: 1 } },
    panelHead: {
        borderBottom: `1px solid ${T.borderSoft}`,
        background: T.surface,
        padding: '12px 20px',
    },
    panelTitle: { margin: 0, fontSize: 16, fontWeight: 600, color: T.fg },
    panelSub: { margin: '2px 0 0', fontSize: 12, color: T.mutedFg },
    panelBody: { padding: 20 },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        flexWrap: 'wrap',
    },
    reports: { display: 'flex', gap: 8, flexWrap: 'wrap' },
    search: {
        width: 'min(100%, 360px)',
        '& input': { height: 42, fontSize: 14, marginBottom: 0 },
    },
    list: { listStyle: 'none', margin: 0, padding: 0, transition: 'opacity .15s ease' },
    listStale: { opacity: 0.5, pointerEvents: 'none' },
    empty: { padding: 24, color: T.mutedFg, fontSize: 14 },
    pager: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        flexWrap: 'wrap',
        padding: '12px 20px',
        borderTop: `1px solid ${T.borderSoft}`,
        fontSize: 13,
        color: T.mutedFg,
    },
});

class SanctionedPlates extends PureComponent {
    /* Last page the server returned, kept so a page change or new search
       re-renders the current rows dimmed instead of dropping to a spinner
       and flashing "Plates: 0" while the next page loads. */
    lastPaged = null;

    lastPagedInstallationId = null;

    applySearch = debounce((search) => {
        const debouncedSearch = search.trim();
        /* A trailing space or a typed-then-deleted character leaves the term
           as it was; do not reset the page for that. */
        if (debouncedSearch === this.props.state.debouncedSearch) return;
        this.props.setState({ debouncedSearch, page: 1 });
    }, SEARCH_DEBOUNCE_MS);

    componentDidMount() {
        this.clampPageToResult();
    }

    componentDidUpdate() {
        this.clampPageToResult();
    }

    componentWillUnmount() {
        this.applySearch.cancel();
    }

    /*
     * The server does not clamp the page, so deleting the last row on the
     * last page (or another user deleting rows before a poll) can leave us
     * asking for a page past the end. Step back to the last real page, or
     * to page 1 when nothing is left, so the next create or poll is visible.
     */
    clampPageToResult() {
        const { listPlates, state, setState } = this.props;
        const paged = listPlates.sanctionedPlatesByInstallation;
        if (listPlates.loading || !paged || paged.items.length > 0) return;
        const lastPage = Math.max(1, paged.totalPages);
        if (state.page !== lastPage) setState({ page: lastPage });
    }

    handleSearchChange = (event) => {
        const search = event.target.value;
        this.props.setState({ search });
        this.applySearch(search);
    };

    handlePageChange = (_event, page) => {
        this.props.setState({ page });
    };

    /*
     * A new plate lands wherever its label sorts, usually not on the page
     * being viewed, so the list would look unchanged. Search for the plate
     * itself so the new row is what the operator sees next.
     */
    handleCreateSuccess = (created) => {
        this.applySearch.cancel();
        const plate = created?.plate || '';
        this.props.setState({ search: plate, debouncedSearch: plate, page: 1 });
        this.props.listPlates.refetch();
    };

    handleOnSubmit = ({ id, plate, label, isEnabled, lotId, isVip, vipContactIds }) => {
        if (lotId === "Default") {
            lotId = null;
        }
        return this.props.updatePlate({
            variables: {
                input: {
                    id,
                    plate,
                    label,
                    isEnabled,
                    lotId,
                    isVip,
                    vipContactIds: isVip ? vipContactIds : [],
                },
            },
        }).then(() => this.props.listPlates.refetch());
    };

    handleOnDelete = (plateId) => {
        this.props.setState({ deleting: plateId });
    };

    handleOnDeleteConfirm = async (plateId) => {
        try {
            await this.props.deletePlate({
                variables: { id: plateId },
            });
            this.props.setState({ deleting: null });
            this.props.listPlates.refetch();
        } catch (err) {
            console.error(err); // eslint-disable-line no-console
        }
    };

    handleOnDeleteCancel = () => {
        this.props.setState({ deleting: null });
    };

    handleDownload = async (url) => {
        const filename = `sanctioned_${moment(Date.now()).format("YYYY-MM-DD")}.xlsx`;
        await downloadWithAuth({
            url,
            filename,
        });
    };

    /* Prefer the live result; while a new page/search loads, fall back to the
       last result for the same hotel so rows and the total stay on screen.
       Nothing is promoted while loading, so a result the HOC might still be
       holding from the previous variables (or hotel) never shows undimmed. */
    currentPage() {
        const { listPlates, installation } = this.props;
        if (listPlates.loading) {
            if (this.lastPaged && this.lastPagedInstallationId === installation.id) {
                return this.lastPaged;
            }
            return EMPTY_PAGE;
        }
        const live = listPlates.sanctionedPlatesByInstallation;
        if (live) {
            this.lastPaged = live;
            this.lastPagedInstallationId = installation.id;
            return live;
        }
        if (this.lastPaged && this.lastPagedInstallationId === installation.id) {
            return this.lastPaged;
        }
        return EMPTY_PAGE;
    }

    renderEmpty() {
        const s = this.props.styles;
        const { debouncedSearch } = this.props.state;
        if (debouncedSearch) {
            return (
                <div className={s.empty}>
                    <h2>No plates match "{debouncedSearch}"</h2>
                    Try a different plate or description.
                </div>
            );
        }
        return (
            <div className={s.empty}>
                <h2>No plates available</h2>
                <PermissionOnly permissions={PERMISSIONS.SANCTIONED_PLATES_MANAGE}>
                    Use the form to create your first sanctioned plate.
                </PermissionOnly>
            </div>
        );
    }

    renderPager(paged, isStale) {
        const s = this.props.styles;
        const { total, page, pageSize, totalPages } = paged;
        if (!total) return null;
        const rangeStart = (page - 1) * pageSize + 1;
        const rangeEnd = Math.min(page * pageSize, total);
        return (
            <div className={s.pager}>
                <span>{isStale ? 'Loading…' : `Showing ${rangeStart}-${rangeEnd} of ${total}`}</span>
                {totalPages > 1 && (
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={this.handlePageChange}
                        color="primary"
                        shape="rounded"
                        size="small"
                    />
                )}
            </div>
        );
    }

    render() {
        const { listPlates, pinpadEnabled, state, installation, facility, allLots, contacts } = this.props;
        const s = this.props.styles;
        const canManage = authManager.hasPermission(PERMISSIONS.SANCTIONED_PLATES_MANAGE, installation.id);
        const showPin = !!(pinpadEnabled && pinpadEnabled.installationPinpadEnabled) && canManage;
        const lotsOptions = allLots ? allLots.allLots : [];
        const paged = this.currentPage();
        const isStale = listPlates.loading && paged !== listPlates.sanctionedPlatesByInstallation;

        return (
            <VipPageShell
                eyebrow="Admin - Configuration"
                title="Sanctioned plates"
                right={<HeaderCount label="Plates" value={paged.total} />}
                maxWidth={1500}
            >
                <Helmet>
                    <title>Sanctioned Plates</title>
                </Helmet>
                <div className={s.stack}>
                    <PermissionOnly permissions={PERMISSIONS.SANCTIONED_PLATES_MANAGE}>
                        <Panel className={s.createPanel}>
                            <div className={s.panelHead}>
                                <h2 className={s.panelTitle}>Add sanctioned plate</h2>
                                <p className={s.panelSub}>Add a plate and optionally assign sanctioned-plate VIP contacts.</p>
                            </div>
                            <div className={s.panelBody}>
                                <CreateForm onCreateSuccess={this.handleCreateSuccess} lotsOptions={lotsOptions} />
                            </div>
                        </Panel>
                    </PermissionOnly>
                    <Panel className={s.listPanel}>
                        <div className={s.panelBody}>
                            <div className={s.toolbar}>
                            <div className={s.search}>
                                <SearchInput
                                    autoFocus
                                    onChange={this.handleSearchChange}
                                    placeholder="Search license plate or description"
                                    value={state.search}
                                />
                            </div>
                                <PermissionOnly permissions={PERMISSIONS.REPORTS_EXPORT}>
                                    <div className={s.reports}>
                                        <Button size="small" onClick={() => this.handleDownload(`${DRIVO_URL}/sanctioned-report/${installation.id}/download?all=true`)}>
                                            Report all
                                        </Button>
                                        <Button size="small" onClick={() => this.handleDownload(`${DRIVO_URL}/sanctioned-report/${installation.id}/download`)}>
                                            Last 3 months
                                        </Button>
                                    </div>
                                </PermissionOnly>
                            </div>
                        </div>
                        <LoadingFork
                            isLoading={listPlates.loading && paged === EMPTY_PAGE}
                            error={listPlates.error}
                            graphqlQuery={contacts}
                            render={() => {
                                if (paged.items.length === 0) {
                                    return this.renderEmpty();
                                }

                                return (
                                    <>
                                        <ul className={isStale ? `${s.list} ${s.listStale}` : s.list}>
                                            {paged.items.map((plate) => (
                                                <PlateRow
                                                    key={plate.id}
                                                    plate={plate}
                                                    lotsOptions={lotsOptions}
                                                    contacts={contacts?.contacts}
                                                    showPin={showPin}
                                                    canManage={canManage}
                                                    deleting={state.deleting === plate.id}
                                                    onSave={this.handleOnSubmit}
                                                    onDelete={this.handleOnDelete}
                                                    onDeleteConfirm={this.handleOnDeleteConfirm}
                                                    facilityId={facility.id}
                                                    onDeleteCancel={this.handleOnDeleteCancel}
                                                />
                                            ))}
                                        </ul>
                                        {this.renderPager(paged, isStale)}
                                    </>
                                );
                            }}
                        />
                    </Panel>
                </div>
            </VipPageShell>
        );
    }
}

function SanctionedPlatesWithStyles(props) {
    const styles = useStyles();
    return <SanctionedPlates {...props} styles={styles} />;
}

export default compose(
    withInstallation,
    /* withState sits above the graphql HOCs so the list query can read the
       debounced search and current page from props.state in its options(). */
    withState(() => ({
        search: "",
        debouncedSearch: "",
        page: 1,
        deleting: null,
    })),
    /**
     * pinpadEnabled HOC is composed BEFORE listPlates so its result is
     * available when listPlates' options() runs — letting us gate the
     * pollInterval on whether this hotel actually has the PIN column
     * visible. Non-pinpad hotels don't poll at all.
     */
    graphql(installationPinpadEnabledQuery, {
        name: 'pinpadEnabled',
        options: (props) => ({
            variables: { installationId: props.installation.id },
        }),
    }),
    graphql(sanctionedPlatesByInstallationQuery, {
        name: 'listPlates',
        options: (props) => ({
            variables: {
                installationId: props.installation.id,
                page: props.state.page,
                pageSize: PAGE_SIZE,
                search: props.state.debouncedSearch || null,
            },
            /* Every page/search is its own cache entry and a delete or edit
               only refetches the current one, so cached pages would keep
               showing removed or renamed plates. Always go to the server. */
            fetchPolicy: 'network-only',
            /**
             * Poll only when Pinpad Access is enabled for this hotel — so
             * the PIN column reflects live provisioning status (pending →
             * provisioned / failed) without a manual refresh. Non-pinpad
             * hotels have the column hidden and don't need to poll.
             * pollInterval 0 means "don't poll".
             */
            pollInterval: props.pinpadEnabled?.installationPinpadEnabled
                ? 5000
                : 0,
        }),
    }),
    graphql(allLots, {
        name: 'allLots',
        options: (props) => ({
            variables: {
                installationId: props.installation.id,
            },
        }),
    }),
    graphql(sanctionedPlateContactsQuery, {
        name: 'contacts',
        options: (props) => ({
            variables: { installationId: props.installation.id },
        }),
    }),
    graphql(updateSanctionedPlateQuery, { name: 'updatePlate' }),
    graphql(deleteSanctionedPlateQuery, { name: 'deletePlate' }),
)(SanctionedPlatesWithStyles);
